import {
  LegendSymbolTypes,
  DEFAULT_LEGEND_SIZES,
  DEFAULT_LEGEND_STYLES,
  SYMBOL_SIZE_PARAMETERS,
} from "./legendDefaults";
import { getDimensions } from "../../../components/SymbolEditor/components/ImageSymbolEditor";
import { getParameterValue } from "../classification/getFromClassified";
import { updateSymbolParameter } from "../classification/updateSymbolParameter";
import { getParameterFromSymbol } from "../classification/getParameterFromStyle";
import { ClassificationParameterExtended } from "../../../interfaces/classification";
import {
  ImageSymbol,
  isMaskedImageSymbol,
  isPolygonSymbol,
  isPolylineSymbols,
  isSizableSymbol,
  isTwoDimensionalSymbol,
  ParameterValue,
  ParameterValueWithSymbol,
  PointSymbol,
  PolygonSymbol,
  SquareSymbol,
  StyleSymbolType,
  isParameterValueSymbol,
  isH3GridSymbol,
  H3Symbol,
  isClusterSymbol,
  ClusterSymbol,
  SimplePolylineSymbol,
} from "../../../interfaces/style";

type SymbolCreator<T> = (
  baseSymbol: T,
  parameter: ClassificationParameterExtended,
  classificationsParams: ClassificationParameterExtended[],
  parameterValue: ParameterValue,
) => T;

const getNonClassifiedParamValue = <S extends StyleSymbolType>(
  baseSymbol: S,
  parameter: ClassificationParameterExtended,
  classificationsParams: ClassificationParameterExtended[],
  type: LegendSymbolTypes,
) => {
  if (classificationsParams.includes(parameter)) {
    const defaultParameterValue = getParameterFromSymbol(DEFAULT_LEGEND_STYLES[type], parameter);

    if (defaultParameterValue) {
      return defaultParameterValue;
    }
    if (SYMBOL_SIZE_PARAMETERS.includes(parameter) && DEFAULT_LEGEND_SIZES[baseSymbol.type]) {
      return DEFAULT_LEGEND_SIZES[baseSymbol.type];
    }
  }

  return getParameterValue(getParameterFromSymbol(baseSymbol, parameter));
};

const createPointSymbol: SymbolCreator<PointSymbol | SquareSymbol> = (
  baseSymbol,
  parameter,
  classificationsParams,
  parameterValue,
): PointSymbol | SquareSymbol => {
  return updateSymbolParameter(
    {
      ...baseSymbol,
      fillColor: getNonClassifiedParamValue(baseSymbol, "fillColor", classificationsParams, LegendSymbolTypes.Point),
      strokeColor: getNonClassifiedParamValue(
        baseSymbol,
        "strokeColor",
        classificationsParams,
        LegendSymbolTypes.Point,
      ),
      strokeWidth: getNonClassifiedParamValue(
        baseSymbol,
        "strokeWidth",
        classificationsParams,
        LegendSymbolTypes.Point,
      ),
      size: getNonClassifiedParamValue(baseSymbol, "size", classificationsParams, LegendSymbolTypes.Point),
      offset: [0, 0],
    },
    parameter,
    parameterValue,
  );
};

const createImagePointSymbol: SymbolCreator<ImageSymbol> = (
  baseSymbol,
  parameter,
  classificationsParams,
  parameterValue,
): ImageSymbol => {
  let symbol = { ...baseSymbol };

  if (isMaskedImageSymbol(symbol)) {
    symbol.maskedColor = getNonClassifiedParamValue(
      baseSymbol,
      "maskedColor",
      classificationsParams,
      LegendSymbolTypes.ImagePoint,
    );
  }

  if (parameter === "height" || parameter === "width") {
    const { height, width } = getDimensions(baseSymbol, parameterValue as number);

    symbol.height = height;
    symbol.width = width;
  } else {
    symbol.width = getNonClassifiedParamValue(baseSymbol, "width", classificationsParams, LegendSymbolTypes.ImagePoint);
    symbol.height = symbol.width;

    symbol = updateSymbolParameter(symbol, parameter, parameterValue);
  }

  symbol.offset = [getParameterValue(symbol.height) / 2, getParameterValue(symbol.width) / 2];

  return symbol;
};

const createLineSymbol: SymbolCreator<SimplePolylineSymbol> = (
  baseSymbol,
  parameter,
  classificationsParams,
  parameterValue,
): SimplePolylineSymbol => {
  return updateSymbolParameter(
    {
      ...baseSymbol,
      type: "simplePolylineSymbol",
      stroke: {
        color: getNonClassifiedParamValue(baseSymbol, "stroke.color", classificationsParams, LegendSymbolTypes.Line),
        width: getNonClassifiedParamValue(baseSymbol, "stroke.width", classificationsParams, LegendSymbolTypes.Line),
      },
    },
    parameter,
    parameterValue,
  );
};

const createPolygonSymbol: SymbolCreator<PolygonSymbol> = (
  baseSymbol,
  parameter,
  classificationsParams,
  parameterValue,
): PolygonSymbol => {
  return updateSymbolParameter(
    {
      ...baseSymbol,
      fill: {
        color: getNonClassifiedParamValue(baseSymbol, "fill.color", classificationsParams, LegendSymbolTypes.Polygon),
        hatchColor: getNonClassifiedParamValue(
          baseSymbol,
          "fill.hatchColor",
          classificationsParams,
          LegendSymbolTypes.Polygon,
        ),
      },
      stroke: {
        color: getNonClassifiedParamValue(baseSymbol, "stroke.color", classificationsParams, LegendSymbolTypes.Polygon),
        width: getNonClassifiedParamValue(baseSymbol, "stroke.width", classificationsParams, LegendSymbolTypes.Polygon),
      },
    },
    parameter,
    parameterValue,
  );
};

const createH3Symbol: SymbolCreator<H3Symbol> = (
  baseSymbol,
  parameter,
  classificationsParams,
  parameterValue,
): H3Symbol => {
  return updateSymbolParameter(
    {
      ...baseSymbol,
      fill: {
        color: getNonClassifiedParamValue(baseSymbol, "fill.color", classificationsParams, LegendSymbolTypes.Polygon),
      },
      stroke: {
        color: getNonClassifiedParamValue(baseSymbol, "stroke.color", classificationsParams, LegendSymbolTypes.Polygon),
        width: getNonClassifiedParamValue(baseSymbol, "stroke.width", classificationsParams, LegendSymbolTypes.Polygon),
      },
    },
    parameter,
    parameterValue,
  );
};

const createClusterSymbol: SymbolCreator<ClusterSymbol> = (
  baseSymbol,
  parameter,
  classificationsParams,
  parameterValue,
): ClusterSymbol => {
  return updateSymbolParameter(
    {
      ...baseSymbol,
      fillColor: getNonClassifiedParamValue(baseSymbol, "fillColor", classificationsParams, LegendSymbolTypes.Point),
    },
    parameter,
    parameterValue,
  );
};

export const createLegendSymbol = (
  parameterValue: ParameterValueWithSymbol,
  parameter: ClassificationParameterExtended,
  classificationsParams: ClassificationParameterExtended[],
  baseSymbol: StyleSymbolType,
): StyleSymbolType => {
  if (isParameterValueSymbol(parameterValue)) {
    return parameterValue;
  }

  if (isClusterSymbol(baseSymbol)) {
    return createClusterSymbol(baseSymbol, parameter, classificationsParams, parameterValue);
  }

  if (isSizableSymbol(baseSymbol)) {
    return createPointSymbol(baseSymbol, parameter, classificationsParams, parameterValue);
  }

  if (isTwoDimensionalSymbol(baseSymbol)) {
    return createImagePointSymbol(baseSymbol, parameter, classificationsParams, parameterValue);
  }

  if (isPolylineSymbols(baseSymbol)) {
    return createLineSymbol(baseSymbol as SimplePolylineSymbol, parameter, classificationsParams, parameterValue);
  }

  if (isPolygonSymbol(baseSymbol)) {
    return createPolygonSymbol(baseSymbol as PolygonSymbol, parameter, classificationsParams, parameterValue);
  }

  if (isH3GridSymbol(baseSymbol)) {
    return createH3Symbol(baseSymbol as H3Symbol, parameter, classificationsParams, parameterValue);
  }
};
